import CDinfo from './detail/CDinfo';
import Button from '../../button/Button';
import {
  UpdateCustomerRequest,
  useCustomerStore,
} from '../../../store/useCustomer';
import { useFranchiseStore } from '../../../store/useFranchise';
import FranchiseInfo from './detail/FranchiseInfo';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PasswordModal from './PasswordModal';
import { formatDate } from '../../../util/util';
import { getFranchiseMailOrderNumber } from '../../../util/franchise';

/**
 * @author taulmax
 * @date 2024.10.07
 * @description 마이페이지 프로필 페이지
 */
const MypageProfile = () => {
  const { myCustomer, updateMyCustomer } = useCustomerStore();
  const { myfranchise, updateMyFranchise } = useFranchiseStore();

  const [loginID, setLoginID] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [customerTelNo, setCustomerTelNo] = useState<string>('');
  const [customerCreatedAt, setCustomerCreatedAt] = useState<string>('');
  const [customerUpdatedAt, setCustomerUpdatedAt] = useState<string>('');

  const [companyName, setCompanyName] = useState<string>('');
  const [ceo, setCeo] = useState<string>('');
  const [companyTelNo, setCompanyTelNo] = useState<string>('');
  const [companyNumber, setCompanyNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [mailOrderNumber, setMailOrderNumber] = useState<string>('');
  const [companyCreatedAt, setCompanyCreatedAt] = useState<string>('');
  const [companyUpdatedAt, setCompanyUpdatedAt] = useState<string>('');
  const [bizProfileName, setBizProfileName] = useState<string>('');

  const [isOpen, setIsOpen] = useState<boolean>(false);

  // 변경 감지
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [initialData, setInitialData] = useState({
    name: '',
    telNo: '',
    mailOrderNumber: '',
  });
  const checkForChanges = useCallback(() => {
    setHasChanged(
      name !== initialData.name ||
      customerTelNo !== initialData.telNo ||
      mailOrderNumber !== initialData.mailOrderNumber
    );
  }, [customerTelNo, initialData, mailOrderNumber, name]);
  const resetChanges = useCallback(() => {
    setName(initialData.name);
    setCustomerTelNo(initialData.telNo);
    setMailOrderNumber(initialData.mailOrderNumber);
    setHasChanged(false);
  }, [initialData]);

  // 변경 사항 체크
  useEffect(() => {
    if (myCustomer && myfranchise) {
      checkForChanges();
    }
  }, [checkForChanges, myCustomer, myfranchise]);

  useEffect(() => {
    if (myCustomer) {
      const { loginId, name, telNo, createdAt, updatedAt } = myCustomer;
      setLoginID(loginId);
      setName(name);
      setCustomerTelNo(telNo);
      setCustomerCreatedAt(formatDate(createdAt).timestamp);
      setCustomerUpdatedAt(formatDate(updatedAt).timestamp);

      setInitialData(prev => ({
        ...prev,
        name,
        telNo,
      }));
    }
  }, [myCustomer]);

  useEffect(() => {
    if (myfranchise) {
      const { companyName, ceo, telNo, companyNumber, createdAt, updatedAt, address, email } =
        myfranchise;
      setCompanyName(companyName);
      setCeo(ceo);
      setCompanyTelNo(telNo);
      setCompanyNumber(companyNumber);
      setCompanyCreatedAt(formatDate(createdAt).timestamp);
      setCompanyUpdatedAt(formatDate(updatedAt).timestamp);
      setAddress(address);
      setEmail(email);
      setBizProfileName(myfranchise.bizProfileName);
      setMailOrderNumber(getFranchiseMailOrderNumber(myfranchise));
      setInitialData(prev => ({
        ...prev,
        mailOrderNumber: getFranchiseMailOrderNumber(myfranchise),
      }));
    }
  }, [myfranchise]);

  const onClickUpdate = useCallback(() => {
    if (!name && !customerTelNo && !mailOrderNumber) {
      return toast.warn('이름, 전화번호 또는 통신판매번호를 입력해주세요');
    }
    const customerChanged =
      name !== initialData.name || customerTelNo !== initialData.telNo;
    const franchiseChanged =
      mailOrderNumber !== initialData.mailOrderNumber;

    if (customerChanged) {
      const customerData: UpdateCustomerRequest = {
        name,
        telNo: customerTelNo,
      };

      updateMyCustomer(customerData);
    }

    if (franchiseChanged) {
      updateMyFranchise({
        mailOrderNumber: mailOrderNumber.trim(),
      });
    }
  }, [
    customerTelNo,
    initialData.mailOrderNumber,
    initialData.name,
    initialData.telNo,
    mailOrderNumber,
    name,
    updateMyFranchise,
    updateMyCustomer,
  ]);

  return (
    <>
      <div className="customer_detail_wrapper">
        {/*내 정보 */}
        <div className="customer_detail_content_wrapper">
          <div className="customer_wrapper">
            <CDinfo
              loginID={loginID}
              name={name}
              setName={setName}
              telNo={customerTelNo}
              setTelNo={setCustomerTelNo}
              createdAt={customerCreatedAt}
              updatedAt={customerUpdatedAt}
            />
            <div className="customer_detail_big_divide"></div>
            <FranchiseInfo
              companyName={companyName}
              ceo={ceo}
              telNo={companyTelNo}
              companyNumber={companyNumber}
              mailOrderNumber={mailOrderNumber}
              setMailOrderNumber={setMailOrderNumber}
              address={address}
              email={email}
              createdAt={companyCreatedAt}
              updatedAt={companyUpdatedAt}
              bizProfileName={bizProfileName}
            />
          </div>
        </div>
      </div>
      <div className="customer_detail_button_wrapper">
        <div
          className="left"
          style={{ display: 'flex', alignItems: 'center' }}
        ></div>
        <div
          className="right"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {hasChanged ? (
            <>
              <p className="change_detected_message">
                * 주문 상세 정보 변경이 감지되었습니다.
              </p>
              <Button
                type="reset"
                text="정보 수정 초기화"
                onClick={resetChanges}
                style={{ marginLeft: 8 }}
              ></Button>
              <Button
                type="update"
                text="정보 수정"
                onClick={onClickUpdate}
                style={{ marginLeft: 8 }}
              ></Button>
            </>
          ) : (
            <>
              <p className="change_detected_message">
                * 정보를 수정하시면 '수정 버튼'이 나타납니다.
              </p>
              <Button
                type="submit"
                text="비밀번호 변경"
                onClick={() => {
                  setIsOpen(true);
                }}
                style={{ marginLeft: 8 }}
              />
            </>
          )}
        </div>
        <PasswordModal
          id={loginID}
          setId={setLoginID}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      </div>
    </>
  );
};

export default MypageProfile;
